import React, { useState, useEffect } from 'react';
import {
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Divider,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useSnackbar from '../../../hooks/useSnackbar';
import { ITool, IToolBag } from '../../../utils/entities/interface';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../components/atoms/Button';
import { CreateTool, CreateToolBag, EditToolBag } from '../../../services/tool';

interface FormData {
  _id?: string;
  toolbagname: string;
  description: string;
}

interface ToolAddEditProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  toolbag?: IToolBag | null;
}
const AddEditToolBagModal = ({
  open,
  onClose,
  onSaved,
  toolbag,
}: ToolAddEditProps) => {
  const { showSnackbar, SnackbarComponent, resetSnackbar } = useSnackbar();
  const [formData, setFormData] = useState<FormData>({
    toolbagname: '',
    description: '',
  });

  useEffect(() => {
    if (toolbag) {
      setFormData({
        _id: toolbag._id,
        toolbagname: toolbag.name || '',
        description: toolbag.description || '',
      });
    } else {
      setFormData({ toolbagname: '', description: '' });
    }
  }, [toolbag]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    console.log('Form Data:', formData);
    try {
      let status;
      if (formData._id) {
        status = await EditToolBag({
          toolBagId: formData._id,
          name: formData.toolbagname,
          description: formData.description,
        });
      } else {
        status = await CreateToolBag({
          name: formData.toolbagname,
          description: formData.description,
        });
      }

      if (status === 200 || status === 201) {
        showSnackbar(
          `Toolbag ${formData._id ? 'updated' : 'created'} successfully!`,
          'success',
        );
        setTimeout(() => {
          onClose();
          resetModal();
          onSaved();
        }, 1500);
      } else {
        showSnackbar(
          `Error: Failed to ${formData._id ? 'update' : 'create'} tool`,
          'error',
        );
      }
    } catch (error: any) {
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };

  const resetModal = () => {
    setFormData({ toolbagname: '', description: '' });
    onClose();
    resetSnackbar();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      sx={{ borderRadius: 10, overflow: 'hidden' }} // Ensuring rounded corners
      PaperProps={{
        sx: {
          borderRadius: 10,
          overflow: 'hidden', // Ensures the background doesn't leak
        },
      }}
    >
      <Box
        sx={{
          border: '1px solid #7B1FA2', // Add border here
          borderRadius: 10,
          overflow: 'hidden', // Prevent border from affecting layout
        }}
      >
        {SnackbarComponent}
        <DialogTitle
          sx={{
            fontSize: '22px',
            fontWeight: 'bold',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#181818',
            padding: '16px',
          }}
        >
          {formData._id ? 'Edit Toolbag' : 'Create Toolbag'}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ backgroundColor: '#181818', padding: '24px' }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography gutterBottom>ToolBag Name</Typography>
              <TextField
                name="toolbagname"
                value={formData.toolbagname}
                onChange={handleChange}
                fullWidth
                required
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography gutterBottom> Description</Typography>
              <TextField
                name="description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                required
                multiline
                minRows={4}
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
          <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
            <SecondaryButton title="Cancel" onClick={onClose} />
            <PrimaryButton title="Save" onClick={(e) => handleSubmit(e)} />
          </DialogActions>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AddEditToolBagModal;
